import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Switch, FormControlLabel,
  Button, Alert, CircularProgress,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, IconButton, Accordion, AccordionSummary, AccordionDetails, Checkbox
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoIcon from '@mui/icons-material/Info';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';

// Third-party social media service definitions
const thirdPartyServices = {
  instagram: {
    name: 'Instagram',
    description: 'Connect Instagram Business account for social media posting and analytics',
    icon: '📸',
    color: '#E4405F',
    supportsOAuth: true,
    supportsApiKey: true
  },
  facebook: {
    name: 'Facebook',
    description: 'Connect Facebook Page for posting, marketplace, and analytics',
    icon: '📘',
    color: '#1877F2',
    supportsOAuth: true,
    supportsApiKey: true
  },
  tiktok: {
    name: 'TikTok',
    description: 'Connect TikTok Business account for video posting',
    icon: '🎵',
    color: '#000000',
    supportsOAuth: true,
    supportsApiKey: true
  },
  google: {
    name: 'Google',
    description: 'Connect Google for Calendar sync, Business Profile, and Support Inbox (Gmail)',
    icon: '🔍',
    color: '#4285F4',
    supportsOAuth: true,
    supportsApiKey: true
  },
  microsoft: {
    name: 'Microsoft',
    description: 'Connect Microsoft Outlook / Microsoft 365 for Support Inbox',
    icon: '🪟',
    color: '#00A4EF',
    supportsOAuth: true,
    supportsApiKey: false
  },
  twitter: {
    name: 'Twitter/X',
    description: 'Connect X (Twitter) to publish posts, images, and videos',
    icon: '🐦',
    color: '#1DA1F2',
    supportsOAuth: true,
    supportsApiKey: false
  },
  pinterest: {
    name: 'Pinterest',
    description: 'Connect Pinterest for product pins and boards',
    icon: '📌',
    color: '#E60023',
    supportsOAuth: true,
    supportsApiKey: false
  }
};

// Payment processor service definitions
const paymentProcessorServices = {
  stripe: {
    name: 'Stripe',
    description: 'Modern payment processing with global reach and instant payouts',
    icon: '💳',
    color: '#635BFF',
    supportsOAuth: true,
    supportsApiKey: true,
    docsURL: 'https://stripe.com/docs/api'
  },
  square: {
    name: 'Square',
    description: 'Integrated payments, POS hardware, and business management tools',
    icon: '⬛',
    color: '#006AFF',
    supportsOAuth: true,
    supportsApiKey: true,
    docsURL: 'https://developer.squareup.com/docs'
  },
  paypal: {
    name: 'PayPal',
    description: 'Accept PayPal payments and access Braintree gateway',
    icon: '🅿️',
    color: '#003087',
    supportsOAuth: true,
    supportsApiKey: true,
    docsURL: 'https://developer.paypal.com/docs'
  },
  clover: {
    name: 'Clover',
    description: 'POS system with integrated payments and business apps',
    icon: '🍀',
    color: '#43B02A',
    supportsOAuth: true,
    supportsApiKey: true,
    docsURL: 'https://docs.clover.com/docs'
  }
};

// Email provider definitions — visibility toggle only (no OAuth/API Key split at this level)
const emailProviderServices = {
  gmail: {
    name: 'Gmail (Google)',
    description: 'OAuth or SMTP via Google account — most common for small businesses',
    icon: '📧',
    color: '#EA4335',
    category: 'Google'
  },
  resend: {
    name: 'Resend',
    description: 'Developer-first transactional email API with excellent deliverability',
    icon: '✉️',
    color: '#000000',
    category: 'Transactional'
  },
  postmark: {
    name: 'Postmark',
    description: 'Fast, reliable transactional email with detailed delivery tracking',
    icon: '📬',
    color: '#FFDE00',
    category: 'Transactional'
  },
  sendgrid: {
    name: 'SendGrid',
    description: 'Scalable email delivery platform by Twilio — widely adopted',
    icon: '📨',
    color: '#1A82E2',
    category: 'Transactional'
  },
  ses: {
    name: 'AWS SES',
    description: 'High-volume, cost-effective email via Amazon Web Services',
    icon: '💵',
    color: '#FF9900',
    category: 'Cloud'
  },
  smtp: {
    name: 'Custom SMTP',
    description: 'Generic SMTP server — use your own email infrastructure',
    icon: '🖥️',
    color: '#607D8B',
    category: 'Custom'
  },
  mailchimp: {
    name: 'Mailchimp',
    description: 'Marketing campaigns, newsletters, and audience automation',
    icon: '🐵',
    color: '#FFE01B',
    category: 'Marketing'
  }
};

const subscriptionTiers = ['free', 'standard', 'professional', 'enterprise'];

// Tax API provider definitions
const taxAPIProviderServices = {
  taxjar: {
    name: 'TaxJar',
    description: 'Automated sales tax calculation, reporting, and filing for online sellers',
    icon: '🧾',
    color: '#FF6B35',
    supportsOAuth: false,
    supportsApiKey: true,
    docsURL: 'https://developers.taxjar.com/api/reference/'
  },
  avalara: {
    name: 'Avalara',
    description: 'Enterprise-grade tax compliance and automation platform',
    icon: '🏢',
    color: '#003087',
    supportsOAuth: false,
    supportsApiKey: true,
    docsURL: 'https://developer.avalara.com/avatax/'
  },
  stripe: {
    name: 'Stripe Tax',
    description: 'Automatic tax calculation integrated with Stripe Connect (OAuth) or Stripe API key',
    icon: '💳',
    color: '#635BFF',
    supportsOAuth: true,
    supportsApiKey: true,
    docsURL: 'https://stripe.com/docs/tax'
  }
};

// AI service provider definitions
// OAuth defaults to false since app is NOT yet registered with any AI OAuth programs
const aiServiceProviders = {
  openai: {
    name: 'OpenAI',
    description: 'GPT-4o (text) + DALL-E (image editing & enhancement)',
    icon: '🧠',
    color: '#412991',
    category: 'Text & Image AI',
    supportsOAuth: true,
    supportsApiKey: true,
    registrationURL: 'https://platform.openai.com/api-keys'
  },
  google_ai: {
    name: 'Google AI (Gemini)',
    description: 'Gemini for text & image analysis (free tier available)',
    icon: '✨',
    color: '#4285F4',
    category: 'Text & Image AI',
    supportsOAuth: true,
    supportsApiKey: true,
    registrationURL: 'https://aistudio.google.com/app/apikey'
  },
  claude: {
    name: 'Claude (Anthropic)',
    description: 'Claude Sonnet and Opus for text generation and analysis',
    icon: '🟠',
    color: '#D97706',
    category: 'Text AI',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://console.anthropic.com/settings/keys'
  },
  deepseek: {
    name: 'DeepSeek',
    description: 'DeepSeek V4 for text generation, reasoning, and analysis',
    icon: '🔷',
    color: '#4D6BFE',
    category: 'Text AI',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://platform.deepseek.com/api_keys'
  },
  kimi: {
    name: 'Kimi (Moonshot)',
    description: 'Kimi K2.6 and K3 for text generation, analysis, and long-context work',
    icon: '🌙',
    color: '#1A1A1A',
    category: 'Text AI',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://platform.moonshot.ai/console/api-keys'
  },
  glm: {
    name: 'GLM (Z.AI)',
    description: 'GLM models for text generation, analysis, and design',
    icon: '🟣',
    color: '#7C3AED',
    category: 'Text AI',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://z.ai/manage-apikey/apikey-list'
  },
  stablediffusion: {
    name: 'Stable Diffusion',
    description: 'Image upscaling, enhancement & generation',
    icon: '🎨',
    color: '#A855F7',
    category: 'Image Enhancement',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://platform.stability.ai/account/keys'
  },
  removebg: {
    name: 'Remove.bg',
    description: 'Background removal for product photos',
    icon: '✂️',
    color: '#2D8CFF',
    category: 'Image Processing',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://www.remove.bg/api#api-get-started'
  },
  photoroom: {
    name: 'Photoroom',
    description: 'Product photo editing and background replacement',
    icon: '📷',
    color: '#FF6B35',
    category: 'Image Processing',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://www.photoroom.com/api'
  },
  runway: {
    name: 'Runway',
    description: 'Image & video enhancement, super-resolution',
    icon: '🎬',
    color: '#00D4AA',
    category: 'Video Enhancement',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://app.runwayml.com/settings/api-keys'
  },
  canva: {
    name: 'Canva AI',
    description: 'Marketing images with text overlays & branding',
    icon: '🎯',
    color: '#00C4CC',
    category: 'Image Enhancement',
    supportsOAuth: true,
    supportsApiKey: true,
    registrationURL: 'https://www.canva.com/developers/'
  },
  murf: {
    name: 'Murf AI',
    description: 'Professional AI voice-overs for video ads',
    icon: '🎙️',
    color: '#6366F1',
    category: 'Voice & Audio',
    supportsOAuth: false,
    supportsApiKey: true,
    registrationURL: 'https://murf.ai/studio/api'
  }
};

export default function ThirdPartyAuthContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Authentication configuration state
  const [authConfig, setAuthConfig] = useState({
    globalEnabled: true,
    services: {}
  });

  // AI services auth configuration state (separate section)
  const [aiAuthConfig, setAiAuthConfig] = useState({
    globalEnabled: true,
    services: {}
  });

  // Payment processor configuration state
  const [paymentConfig, setPaymentConfig] = useState({
    globalEnabled: true,
    services: {}
  });

  // Email provider configuration state
  const [emailConfig, setEmailConfig] = useState({
    globalEnabled: true,
    services: {}
  });

  // Tax API provider configuration state
  const [taxProviderConfig, setTaxProviderConfig] = useState({
    globalEnabled: true,
    services: {}
  });

  // Initialize social media services with defaults
  const initializeServices = useCallback((existingServices = {}) => {
    const services = {};
    Object.keys(thirdPartyServices).forEach(serviceKey => {
      const serviceDef = thirdPartyServices[serviceKey];
      services[serviceKey] = {
        enabled: existingServices[serviceKey]?.enabled ?? true,
        enableOAuth: existingServices[serviceKey]?.enableOAuth ?? serviceDef.supportsOAuth,
        enableApiKey: existingServices[serviceKey]?.enableApiKey ?? serviceDef.supportsApiKey,
        enableDirectPosting: existingServices[serviceKey]?.enableDirectPosting ?? false,
        displayName: existingServices[serviceKey]?.displayName ?? serviceDef.name,
        description: existingServices[serviceKey]?.description ?? serviceDef.description,
        icon: existingServices[serviceKey]?.icon ?? serviceDef.icon
      };
    });
    return services;
  }, []);

  // Initialize AI services with defaults — OAuth disabled by default (not yet registered)
  const initializeAIServices = useCallback((existingServices = {}) => {
    const services = {};
    Object.keys(aiServiceProviders).forEach(serviceKey => {
      const serviceDef = aiServiceProviders[serviceKey];
      services[serviceKey] = {
        enabled: existingServices[serviceKey]?.enabled ?? true,
        enableOAuth: existingServices[serviceKey]?.enableOAuth ?? false, // OAuth OFF by default
        enableApiKey: existingServices[serviceKey]?.enableApiKey ?? true, // API Key ON by default
        displayName: existingServices[serviceKey]?.displayName ?? serviceDef.name,
        description: existingServices[serviceKey]?.description ?? serviceDef.description,
        icon: existingServices[serviceKey]?.icon ?? serviceDef.icon,
        category: existingServices[serviceKey]?.category ?? serviceDef.category
      };
    });
    return services;
  }, []);

  // Initialize payment processor services with defaults
  const initializePaymentServices = useCallback((existingServices = {}) => {
    const services = {};
    Object.keys(paymentProcessorServices).forEach(serviceKey => {
      const serviceDef = paymentProcessorServices[serviceKey];
      services[serviceKey] = {
        enabled: existingServices[serviceKey]?.enabled ?? true,
        enableOAuth: existingServices[serviceKey]?.enableOAuth ?? true,
        enableApiKey: existingServices[serviceKey]?.enableApiKey ?? true,
        displayName: existingServices[serviceKey]?.displayName ?? serviceDef.name,
        description: existingServices[serviceKey]?.description ?? serviceDef.description,
        icon: existingServices[serviceKey]?.icon ?? serviceDef.icon
      };
    });
    return services;
  }, []);

  // Initialize tax API provider services with defaults
  const initializeTaxProviderServices = useCallback((existingServices = {}) => {
    const services = {};
    Object.keys(taxAPIProviderServices).forEach(serviceKey => {
      const serviceDef = taxAPIProviderServices[serviceKey];
      services[serviceKey] = {
        enabled: existingServices[serviceKey]?.enabled ?? true,
        enableOAuth: existingServices[serviceKey]?.enableOAuth ?? serviceDef.supportsOAuth,
        enableApiKey: existingServices[serviceKey]?.enableApiKey ?? serviceDef.supportsApiKey,
        displayName: existingServices[serviceKey]?.displayName ?? serviceDef.name,
        description: existingServices[serviceKey]?.description ?? serviceDef.description,
        icon: existingServices[serviceKey]?.icon ?? serviceDef.icon
      };
    });
    return services;
  }, []);

  // Initialize email provider services with defaults
  const initializeEmailServices = useCallback((existingServices = {}) => {
    const services = {};
    Object.keys(emailProviderServices).forEach(serviceKey => {
      const serviceDef = emailProviderServices[serviceKey];
      services[serviceKey] = {
        enabled: existingServices[serviceKey]?.enabled ?? true,
        availableInTiers: existingServices[serviceKey]?.availableInTiers ?? [...subscriptionTiers],
        displayName: existingServices[serviceKey]?.displayName ?? serviceDef.name,
        description: existingServices[serviceKey]?.description ?? serviceDef.description,
        icon: existingServices[serviceKey]?.icon ?? serviceDef.icon
      };
    });
    return services;
  }, []);

  const fetchConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/config/third-party-auth');
      const data = await response.json();

      if (data.success) {
        const config = data.thirdPartyAuthConfig || {};
        setAuthConfig({
          globalEnabled: config.globalEnabled ?? true,
          services: initializeServices(config.services)
        });
        const aiConfig = data.aiAuthConfig || {};
        setAiAuthConfig({
          globalEnabled: aiConfig.globalEnabled ?? true,
          services: initializeAIServices(aiConfig.services)
        });
        const pConfig = data.paymentProcessorConfig || {};
        setPaymentConfig({
          globalEnabled: pConfig.globalEnabled ?? true,
          services: initializePaymentServices(pConfig.services)
        });
        const eConfig = data.emailProviderConfig || {};
        setEmailConfig({
          globalEnabled: eConfig.globalEnabled ?? true,
          services: initializeEmailServices(eConfig.services)
        });
        const tConfig = data.taxProviderConfig || {};
        setTaxProviderConfig({
          globalEnabled: tConfig.globalEnabled ?? true,
          services: initializeTaxProviderServices(tConfig.services)
        });
      } else {
        setAuthConfig({ globalEnabled: true, services: initializeServices({}) });
        setAiAuthConfig({ globalEnabled: true, services: initializeAIServices({}) });
        setPaymentConfig({ globalEnabled: true, services: initializePaymentServices({}) });
        setEmailConfig({ globalEnabled: true, services: initializeEmailServices({}) });
        setTaxProviderConfig({ globalEnabled: true, services: initializeTaxProviderServices({}) });
      }
    } catch (err) {
      setError('Failed to load authentication configuration: ' + err.message);
      setAuthConfig({ globalEnabled: true, services: initializeServices({}) });
      setAiAuthConfig({ globalEnabled: true, services: initializeAIServices({}) });
      setPaymentConfig({ globalEnabled: true, services: initializePaymentServices({}) });
      setEmailConfig({ globalEnabled: true, services: initializeEmailServices({}) });
      setTaxProviderConfig({ globalEnabled: true, services: initializeTaxProviderServices({}) });
    } finally {
      setLoading(false);
    }
  }, [initializeServices, initializeAIServices, initializePaymentServices, initializeEmailServices, initializeTaxProviderServices]);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  const handleGlobalToggle = (enabled) => {
    setAuthConfig(prev => ({
      ...prev,
      globalEnabled: enabled
    }));
  };

  const handleServiceToggle = (serviceKey, field, value) => {
    setAuthConfig(prev => {
      const updatedServices = {
        ...prev.services,
        [serviceKey]: {
          ...prev.services[serviceKey],
          [field]: value
        }
      };

      // Validation: If enabling service, ensure at least one auth method is enabled
      if (field === 'enabled' && value) {
        const service = updatedServices[serviceKey];
        if (!service.enableOAuth && !service.enableApiKey) {
          updatedServices[serviceKey].enableOAuth = true;
        }
      }

      return {
        ...prev,
        services: updatedServices
      };
    });
  };

  const handleAIGlobalToggle = (enabled) => {
    setAiAuthConfig(prev => ({ ...prev, globalEnabled: enabled }));
  };

  const handleAIServiceToggle = (serviceKey, field, value) => {
    setAiAuthConfig(prev => {
      const updatedServices = {
        ...prev.services,
        [serviceKey]: { ...prev.services[serviceKey], [field]: value }
      };
      if (field === 'enabled' && value) {
        const service = updatedServices[serviceKey];
        if (!service.enableOAuth && !service.enableApiKey) {
          updatedServices[serviceKey].enableApiKey = true;
        }
      }
      return { ...prev, services: updatedServices };
    });
  };

  const handlePaymentGlobalToggle = (enabled) => {
    setPaymentConfig(prev => ({ ...prev, globalEnabled: enabled }));
  };

  const handlePaymentServiceToggle = (serviceKey, field, value) => {
    setPaymentConfig(prev => {
      const updatedServices = {
        ...prev.services,
        [serviceKey]: { ...prev.services[serviceKey], [field]: value }
      };
      if (field === 'enabled' && value) {
        const service = updatedServices[serviceKey];
        if (!service.enableOAuth && !service.enableApiKey) {
          updatedServices[serviceKey].enableApiKey = true;
        }
      }
      return { ...prev, services: updatedServices };
    });
  };

  const handleEmailGlobalToggle = (enabled) => {
    setEmailConfig(prev => ({ ...prev, globalEnabled: enabled }));
  };

  const handleTaxProviderGlobalToggle = (enabled) => {
    setTaxProviderConfig(prev => ({ ...prev, globalEnabled: enabled }));
  };

  const handleTaxProviderServiceToggle = (serviceKey, field, value) => {
    setTaxProviderConfig(prev => {
      const updatedServices = {
        ...prev.services,
        [serviceKey]: { ...prev.services[serviceKey], [field]: value }
      };
      if (field === 'enabled' && value) {
        const service = updatedServices[serviceKey];
        if (!service.enableOAuth && !service.enableApiKey) {
          updatedServices[serviceKey].enableApiKey = true;
        }
      }
      return { ...prev, services: updatedServices };
    });
  };

  const handleEmailServiceToggle = (serviceKey, field, value) => {
    setEmailConfig(prev => {
      const currentService = prev.services[serviceKey] || {};
      const updatedService = {
        ...currentService,
        [field]: value
      };

      if (field === 'enabled') {
        updatedService.availableInTiers = value ? [...subscriptionTiers] : [];
      }

      return {
        ...prev,
        services: {
          ...prev.services,
          [serviceKey]: updatedService
        }
      };
    });
  };

  const handleEmailTierToggle = (serviceKey, tier) => {
    setEmailConfig(prev => {
      const service = prev.services[serviceKey] || {};
      const currentTiers = service.availableInTiers || [];
      const isSelected = currentTiers.includes(tier);
      const nextTiers = isSelected
        ? currentTiers.filter(t => t !== tier)
        : [...currentTiers, tier];

      return {
        ...prev,
        services: {
          ...prev.services,
          [serviceKey]: {
            ...service,
            availableInTiers: nextTiers
          }
        }
      };
    });
  };

  // Validation function
  const validateConfiguration = () => {
    const errors = [];

    Object.entries(authConfig.services).forEach(([serviceKey, service]) => {
      if (service.enabled && !service.enableOAuth && !service.enableApiKey) {
        const def = thirdPartyServices[serviceKey];
        if (def) errors.push(`${def.name}: At least one authentication method must be enabled`);
      }
    });

    Object.entries(aiAuthConfig.services).forEach(([serviceKey, service]) => {
      if (service.enabled && !service.enableOAuth && !service.enableApiKey) {
        const def = aiServiceProviders[serviceKey];
        if (def) errors.push(`${def.name}: At least one authentication method must be enabled`);
      }
    });

    Object.entries(paymentConfig.services).forEach(([serviceKey, service]) => {
      if (service.enabled && !service.enableOAuth && !service.enableApiKey) {
        const def = paymentProcessorServices[serviceKey];
        if (def) errors.push(`${def.name}: At least one connection method (OAuth or API Key) must be enabled`);
      }
    });

    Object.entries(taxProviderConfig.services).forEach(([serviceKey, service]) => {
      if (service.enabled && !service.enableOAuth && !service.enableApiKey) {
        const def = taxAPIProviderServices[serviceKey];
        if (def) errors.push(`${def.name}: At least one connection method (OAuth or API Key) must be enabled`);
      }
    });

    Object.entries(emailConfig.services).forEach(([serviceKey, service]) => {
      if (service.enabled && (!Array.isArray(service.availableInTiers) || service.availableInTiers.length === 0)) {
        const def = emailProviderServices[serviceKey];
        if (def) errors.push(`${def.name}: At least one tier must be enabled when the provider is enabled`);
      }
    });

    return errors;
  };

  const handleSave = async () => {
    const validationErrors = validateConfiguration();

    if (validationErrors.length > 0) {
      setError('Validation failed:\n' + validationErrors.join('\n'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Save social + AI config together
      const authResponse = await fetch('/api/config/third-party-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thirdPartyAuthConfig: authConfig, aiAuthConfig })
      });

      // Save payment processor config
      const paymentResponse = await fetch('/api/config/payment-processor-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentProcessorConfig: paymentConfig })
      });

      // Save email provider config
      const emailResponse = await fetch('/api/config/email-provider-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailProviderConfig: emailConfig })
      });

      // Save tax provider config
      const taxResponse = await fetch('/api/config/tax-provider-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taxProviderConfig })
      });

      const [authData, paymentData, emailData, taxData] = await Promise.all([
        authResponse.json(), paymentResponse.json(), emailResponse.json(), taxResponse.json()
      ]);

      if (authData.success && paymentData.success && emailData.success && taxData.success) {
        setSuccess('All configurations saved! Changes will sync to iOS apps within the next hour.');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        const errors = [
          !authData.success && (authData.error || 'Failed to save social/AI config'),
          !paymentData.success && (paymentData.error || 'Failed to save payment config'),
          !emailData.success && (emailData.error || 'Failed to save email config'),
          !taxData.success && (taxData.error || 'Failed to save tax provider config')
        ].filter(Boolean);
        setError(errors.join('\n'));
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getAuthMethodStatus = (service) => {
    if (!service.enabled) return 'disabled';
    if (service.enableOAuth && service.enableApiKey) return 'both';
    if (service.enableOAuth) return 'oauth';
    if (service.enableApiKey) return 'apikey';
    return 'none';
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'both':
        return <Chip label="OAuth + API Key" color="success" size="small" />;
      case 'oauth':
        return <Chip label="OAuth Only" color="primary" size="small" />;
      case 'apikey':
        return <Chip label="API Key Only" color="secondary" size="small" />;
      case 'disabled':
        return <Chip label="Disabled" color="default" size="small" />;
      case 'none':
        return <Chip label="Invalid" color="error" size="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading authentication configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>🔐 Third-Party Authentication Method Control</strong> - Configure which authentication 
        methods (OAuth or API Key) are available for each third-party service integration in the iOS app.
        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
          <LinkIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Changes sync to iOS apps within the standard 1-hour cache refresh cycle.
        </Box>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</pre>
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Global Control */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Global Third-Party Integration Control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Master switch to enable or disable all third-party integrations.
              When disabled, all integration options will be hidden from the iOS app.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={authConfig.globalEnabled}
                onChange={(e) => handleGlobalToggle(e.target.checked)}
                color="success"
                size="large"
              />
            }
            label={authConfig.globalEnabled ? 'ENABLED' : 'DISABLED'}
            labelPlacement="start"
          />
        </Box>
      </Paper>


      {/* Social Media Integration */}
      <Accordion defaultExpanded={false} sx={{ mb: 3, opacity: authConfig.globalEnabled ? 1 : 0.6 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📱 Social Media Integration</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: 1 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure which authentication methods are available for each service.
              {!authConfig.globalEnabled && (
                <strong> (Disabled - Enable global integrations to configure services)</strong>
              )}
            </Typography>
          </Box>

          <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Service</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  Enabled
                  <Tooltip title="When disabled, the integration tile is hidden from the iOS app">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  <LockOpenIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  OAuth
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  <VpnKeyIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  API Key
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>
                  Direct Post
                  <Tooltip title="When enabled, the iOS app shows a 'Post Now' button that posts directly via API. Requires Meta App Review for Facebook/Instagram. Default: Off (Share Sheet used instead).">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(thirdPartyServices).map(([serviceKey, serviceDef]) => {
                const service = authConfig.services[serviceKey] || {};
                const status = getAuthMethodStatus(service);
                
                return (
                  <TableRow key={serviceKey} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          fontSize: 24,
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          bgcolor: `${serviceDef.color}20`
                        }}>
                          {serviceDef.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {serviceDef.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {serviceDef.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enabled || false}
                        onChange={(e) => handleServiceToggle(serviceKey, 'enabled', e.target.checked)}
                        disabled={!authConfig.globalEnabled}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableOAuth || false}
                        onChange={(e) => handleServiceToggle(serviceKey, 'enableOAuth', e.target.checked)}
                        disabled={!authConfig.globalEnabled || !service.enabled || !serviceDef.supportsOAuth}
                        color="primary"
                      />
                      {!serviceDef.supportsOAuth && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableApiKey || false}
                        onChange={(e) => handleServiceToggle(serviceKey, 'enableApiKey', e.target.checked)}
                        disabled={!authConfig.globalEnabled || !service.enabled || !serviceDef.supportsApiKey}
                        color="secondary"
                      />
                      {!serviceDef.supportsApiKey && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableDirectPosting || false}
                        onChange={(e) => handleServiceToggle(serviceKey, 'enableDirectPosting', e.target.checked)}
                        disabled={!authConfig.globalEnabled || !service.enabled}
                        color="warning"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(status)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* AI Services Authentication Configuration */}
      <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 1 }}>
            <Typography variant="h6">🤖 AI Service Authentication Methods</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={aiAuthConfig.globalEnabled}
                  onChange={(e) => handleAIGlobalToggle(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              }
              label="AI Services Enabled"
              labelPlacement="start"
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: 1 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Control how users authenticate with AI providers. API Key (BYOK) is the default.
              OAuth should only be enabled once the app is officially registered with the provider.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Default Policy:</strong> All AI OAuth options are disabled by default until the app is officially registered
              with each provider's OAuth program. API Key entry (BYOK) is enabled by default — users provide their own keys.
            </Alert>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>AI Service</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>
                  Enabled
                  <Tooltip title="When disabled, the AI provider is hidden from the iOS app's AI API Keys screen">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>
                  <LockOpenIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  OAuth
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>
                  <VpnKeyIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  API Key
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(aiServiceProviders).map(([serviceKey, serviceDef]) => {
                const service = aiAuthConfig.services[serviceKey] || {};
                const status = getAuthMethodStatus(service);

                return (
                  <TableRow key={serviceKey} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          fontSize: 24,
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          bgcolor: `${serviceDef.color}20`
                        }}>
                          {serviceDef.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {serviceDef.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {serviceDef.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={serviceDef.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enabled || false}
                        onChange={(e) => handleAIServiceToggle(serviceKey, 'enabled', e.target.checked)}
                        disabled={!aiAuthConfig.globalEnabled}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableOAuth || false}
                        onChange={(e) => handleAIServiceToggle(serviceKey, 'enableOAuth', e.target.checked)}
                        disabled={!aiAuthConfig.globalEnabled || !service.enabled || !serviceDef.supportsOAuth}
                        color="primary"
                      />
                      {!serviceDef.supportsOAuth && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableApiKey || false}
                        onChange={(e) => handleAIServiceToggle(serviceKey, 'enableApiKey', e.target.checked)}
                        disabled={!aiAuthConfig.globalEnabled || !service.enabled || !serviceDef.supportsApiKey}
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(status)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Payment Processors Configuration */}
      <Accordion defaultExpanded={false} sx={{ mb: 3, opacity: paymentConfig.globalEnabled ? 1 : 0.6 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 1 }}>
            <Typography variant="h6">💳 Payment Processor Configuration</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentConfig.globalEnabled}
                  onChange={(e) => handlePaymentGlobalToggle(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              }
              label="Payments Enabled"
              labelPlacement="start"
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: 1 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Control which payment processors are visible in the iOS app and whether users connect
              via OAuth (recommended) or by entering an API key manually.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Payment Processor</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  Enabled
                  <Tooltip title="When disabled, this processor is hidden from the iOS app's payment settings">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  <LockOpenIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  OAuth
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  <VpnKeyIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  API Key
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(paymentProcessorServices).map(([serviceKey, serviceDef]) => {
                const service = paymentConfig.services[serviceKey] || {};
                const status = getAuthMethodStatus(service);
                return (
                  <TableRow key={serviceKey} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          fontSize: 24, width: 40, height: 40,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: 1, bgcolor: `${serviceDef.color}20`
                        }}>
                          {serviceDef.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{serviceDef.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{serviceDef.description}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enabled || false}
                        onChange={(e) => handlePaymentServiceToggle(serviceKey, 'enabled', e.target.checked)}
                        disabled={!paymentConfig.globalEnabled}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableOAuth || false}
                        onChange={(e) => handlePaymentServiceToggle(serviceKey, 'enableOAuth', e.target.checked)}
                        disabled={!paymentConfig.globalEnabled || !service.enabled || !serviceDef.supportsOAuth}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableApiKey || false}
                        onChange={(e) => handlePaymentServiceToggle(serviceKey, 'enableApiKey', e.target.checked)}
                        disabled={!paymentConfig.globalEnabled || !service.enabled || !serviceDef.supportsApiKey}
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell align="center">{getStatusChip(status)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Email Providers Configuration */}
      <Accordion defaultExpanded={false} sx={{ mb: 3, opacity: emailConfig.globalEnabled ? 1 : 0.6 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 1 }}>
            <Typography variant="h6">📧 Email Provider Configuration</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={emailConfig.globalEnabled}
                  onChange={(e) => handleEmailGlobalToggle(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              }
              label="Email Enabled"
              labelPlacement="start"
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: 1 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Control which email service providers appear in the iOS app's email settings.
              Disabled providers are completely hidden from the provider picker.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Email Provider</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '14%' }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '14%' }}>
                  Visible in App
                  <Tooltip title="When disabled, this provider is hidden from the email provider picker in the iOS app">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '28%' }}>
                  Tier Access
                  <Tooltip title="Choose which subscription tiers can use this provider">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '14%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(emailProviderServices).map(([serviceKey, serviceDef]) => {
                const service = emailConfig.services[serviceKey] || {};
                const availableInTiers = service.availableInTiers || [];
                return (
                  <TableRow key={serviceKey} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          fontSize: 24, width: 40, height: 40,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: 1, bgcolor: `${serviceDef.color}20`
                        }}>
                          {serviceDef.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{serviceDef.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{serviceDef.description}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={serviceDef.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enabled || false}
                        onChange={(e) => handleEmailServiceToggle(serviceKey, 'enabled', e.target.checked)}
                        disabled={!emailConfig.globalEnabled}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {subscriptionTiers.map(tier => {
                          const selected = availableInTiers.includes(tier);
                          return (
                            <Box key={tier} sx={{ display: 'flex', alignItems: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={selected}
                                onChange={() => handleEmailTierToggle(serviceKey, tier)}
                                disabled={!emailConfig.globalEnabled || !service.enabled}
                              />
                              <Chip
                                label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                                size="small"
                                variant={selected ? 'filled' : 'outlined'}
                                color={tier === 'free' ? 'default' : tier === 'standard' ? 'info' : tier === 'professional' ? 'primary' : 'secondary'}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {service.enabled
                        ? <Chip label={`${availableInTiers.length} tiers`} color="success" size="small" />
                        : <Chip label="Hidden" color="default" size="small" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Tax API Providers Configuration */}
      <Accordion defaultExpanded={false} sx={{ mb: 3, opacity: taxProviderConfig.globalEnabled ? 1 : 0.6 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 1 }}>
            <Typography variant="h6">🧾 Tax API Provider Configuration</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={taxProviderConfig.globalEnabled}
                  onChange={(e) => handleTaxProviderGlobalToggle(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              }
              label="Tax Providers Enabled"
              labelPlacement="start"
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pt: 1 }}>
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Control which Tax API Providers are visible in the iOS app and how users connect.
              Stripe Tax supports both OAuth (via Stripe Connect) and direct API key entry.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Tax Provider</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  Enabled
                  <Tooltip title="When disabled, this provider is hidden from the iOS app's Tax API Provider settings">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  <LockOpenIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  OAuth
                  <Tooltip title="Stripe Tax only: Connect via Stripe OAuth (Stripe Connect)">
                    <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>
                  <VpnKeyIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  API Key
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(taxAPIProviderServices).map(([serviceKey, serviceDef]) => {
                const service = taxProviderConfig.services[serviceKey] || {};
                const status = getAuthMethodStatus(service);
                return (
                  <TableRow key={serviceKey} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          fontSize: 24, width: 40, height: 40,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: 1, bgcolor: `${serviceDef.color}20`
                        }}>
                          {serviceDef.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{serviceDef.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{serviceDef.description}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enabled || false}
                        onChange={(e) => handleTaxProviderServiceToggle(serviceKey, 'enabled', e.target.checked)}
                        disabled={!taxProviderConfig.globalEnabled}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {serviceDef.supportsOAuth ? (
                        <Switch
                          checked={service.enableOAuth || false}
                          onChange={(e) => handleTaxProviderServiceToggle(serviceKey, 'enableOAuth', e.target.checked)}
                          disabled={!taxProviderConfig.globalEnabled || !service.enabled}
                          color="primary"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={service.enableApiKey || false}
                        onChange={(e) => handleTaxProviderServiceToggle(serviceKey, 'enableApiKey', e.target.checked)}
                        disabled={!taxProviderConfig.globalEnabled || !service.enabled}
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell align="center">{getStatusChip(status)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          startIcon={<RestoreIcon />} 
          onClick={fetchConfiguration}
          disabled={saving}
        >
          Reset
        </Button>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />} 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}

